import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../redux/store';
import { selectPlannedMealsForRecipe } from '../redux/mealPlanSlice';
import type { PlannedMeal, MealType } from '../redux/mealPlanSlice';
import { formatLocalDateISO } from '../utils/receiptProcessor';

export interface RecipePlanStatus {
  /** True when the recipe sits in an upcoming, not-yet-cooked meal slot. */
  isPlanned: boolean;
  /** Human label for that slot, e.g. "Mercredi soir". */
  slotLabel?: string;
  /** ISO date (YYYY-MM-DD) of that slot — used by "Voir au plan". */
  plannedDate?: string;
  /** Meal type of that slot — the move sheet opens with it marked. */
  plannedMealType?: MealType;
  /** Id of the planned meal itself — what `moveMeal` / `removeFromWeek` act on. */
  plannedMealId?: string;
}

/** Stable empty reference so the selector does not churn when there is no id. */
const EMPTY: PlannedMeal[] = [];

const MEAL_TYPE_KEYS: Record<MealType, { key: string; fr: string; en: string }> = {
  breakfast: { key: 'recipeDetail.slotMorning', fr: 'matin', en: 'breakfast' },
  lunch: { key: 'recipeDetail.slotNoon', fr: 'midi', en: 'lunch' },
  dinner: { key: 'recipeDetail.slotEvening', fr: 'soir', en: 'dinner' },
};

/**
 * Picks the next upcoming, uncooked slot for a recipe. `selectPlannedMealsForRecipe`
 * returns most-recent-first, so we filter to future slots and take the earliest —
 * "the next time you're making this", not the furthest one out.
 */
export function pickUpcomingSlot(
  plannedMeals: PlannedMeal[],
  todayISO: string
): PlannedMeal | undefined {
  return plannedMeals
    .filter(m => m.status !== 'cooked' && !!m.scheduledFor && m.scheduledFor >= todayISO)
    .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor))[0];
}

export function useRecipePlanStatus(recipeId?: string): RecipePlanStatus {
  const { t, i18n } = useTranslation();
  const plannedMeals = useSelector((state: RootState) =>
    recipeId ? selectPlannedMealsForRecipe(state, recipeId) : EMPTY
  );

  return useMemo(() => {
    if (!recipeId || plannedMeals.length === 0) return { isPlanned: false };

    const slot = pickUpcomingSlot(plannedMeals, formatLocalDateISO(new Date()));
    if (!slot) return { isPlanned: false };

    const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
    let weekday = '';
    try {
      weekday = new Date(`${slot.scheduledFor}T12:00:00`).toLocaleDateString(locale, {
        weekday: 'long',
      });
    } catch {
      weekday = '';
    }
    const capitalized = weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : '';

    const meal = MEAL_TYPE_KEYS[slot.mealType];
    const suffix = meal
      ? t(meal.key, { defaultValue: locale === 'fr-FR' ? meal.fr : meal.en })
      : '';

    const slotLabel = [capitalized, suffix].filter(Boolean).join(' ') || undefined;

    return {
      isPlanned: true,
      slotLabel,
      plannedDate: slot.scheduledFor,
      plannedMealType: slot.mealType,
      plannedMealId: slot.id,
    };
  }, [recipeId, plannedMeals, i18n.language, t]);
}

export default useRecipePlanStatus;
